/**
 * AuthContext — Clerk-backed auth with Supabase data layer.
 *
 * Sign-in / sign-up / sign-out → Clerk
 * Database access                → Supabase (JWT from Clerk via "supabase" template)
 * Waitlist signup (no password)  → Supabase edge function (stores interest only,
 *                                   does NOT create a Clerk account)
 */

import {
  createContext,
  useContext,
  useCallback,
  useState,
  useEffect,
  useRef,
  ReactNode,
} from "react";
import {
  useAuth,
  useUser,
  useClerk,
  useSignIn,
  useSignUp,
} from "@clerk/clerk-react";
import { projectId, publicAnonKey } from "../../utils/supabaseInfo";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface StudentProfile {
  studentEmail: string;
  course: string;
  accommodation: string;
  university: string;
  yearOfStudy?: string;
  onboardingComplete?: boolean;
  // email verification
  studentEmailVerified?: boolean;
  studentEmailToken?: string; // present = verification email was sent, pending click
}

interface User {
  id?: string;
  name: string;
  email: string;
  avatar?: string;
  website?: string;
  studentProfile?: StudentProfile;
  socialMedia?: {
    instagram?: string;
    facebook?: string;
	    snapchat?: string;
	    youtube?: string;
	  };
  status?: string;
  currentLocation?: string;
  friends?: {
    id: string;
    name: string;
    avatar?: string;
    university?: string;
    location?: string;
  }[];
}

export interface ProfileUpdate {
  yearOfStudy?: string;
  university?: string;
  studentEmail?: string;
  website?: string;
  onboardingComplete?: boolean;
  // social usernames (stored without @)
  instagram?: string;
  facebook?: string;
  snapchat?: string;
  // email verification
  studentEmailVerified?: boolean;
  studentEmailToken?: string | null; // null = clear the token
  // name (Clerk first/last)
  firstName?: string;
  lastName?: string;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  loginWithGoogle: () => Promise<void>;
  updateProfile: (data: ProfileUpdate) => Promise<{ success: boolean; error?: string }>;
  sendVerificationEmail: (studentEmail: string) => Promise<{ success: boolean; error?: string }>;
  signup: (
    email: string,
    password: string | undefined,
    name: string,
    profile?: Record<string, any>
  ) => Promise<{ success: boolean; error?: string; needsVerification?: boolean }>;
  confirmSignupCode: (code: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  isLoading: boolean;
}

// ─── Context ─────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const EDGE_FN = import.meta.env.VITE_SUPABASE_EDGE_FN || "make-server-50e3cb91";
const API_BASE = `https://${projectId}.supabase.co/functions/v1/${EDGE_FN}`;

// ─── No-auth fallback value (used when Clerk key is missing) ─────────────────

const NO_AUTH_VALUE: AuthContextType = {
  user: null,
  login: async () => ({ success: false, error: "Auth unavailable." }),
  loginWithGoogle: async () => {},
  updateProfile: async () => ({ success: false, error: "Auth unavailable." }),
  sendVerificationEmail: async () => ({ success: false, error: "Auth unavailable." }),
  signup: async () => ({ success: false, error: "Auth unavailable." }),
  confirmSignupCode: async () => ({ success: false, error: "Auth unavailable." }),
  logout: async () => {},
  isAuthenticated: false,
  isLoading: false,
};

// ─── Provider ────────────────────────────────────────────────────────────────

// Exported provider — guards against missing Clerk key so hooks are never
// called outside a valid ClerkProvider context.
export function AuthProvider({ children }: { children: ReactNode }) {
  const hasClerkKey = Boolean(import.meta.env.VITE_CLERK_PUBLISHABLE_KEY);
  if (!hasClerkKey) {
    return (
      <AuthContext.Provider value={NO_AUTH_VALUE}>
        {children}
      </AuthContext.Provider>
    );
  }
  return <ClerkAuthProvider>{children}</ClerkAuthProvider>;
}

// Internal component — only rendered when ClerkProvider is in the tree.
function ClerkAuthProvider({ children }: { children: ReactNode }) {
  // Clerk state
  const { isLoaded: authLoaded, isSignedIn } = useAuth();
  const { user: clerkUser, isLoaded: userLoaded } = useUser();
  const { signOut } = useClerk();
  const { signIn, isLoaded: signInLoaded, setActive: setSignInActive } = useSignIn();
  const { signUp, isLoaded: signUpLoaded, setActive: setSignUpActive } = useSignUp();

  const clerkLoaded = authLoaded && userLoaded && signInLoaded && signUpLoaded;

  // If Clerk hasn't loaded after 5 seconds, fall through so the site is usable.
  const [timedOut, setTimedOut] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!clerkLoaded && !timedOut) {
      timeoutRef.current = setTimeout(() => setTimedOut(true), 5_000);
    }
    return () => { if (timeoutRef.current) clearTimeout(timeoutRef.current); };
  }, [clerkLoaded, timedOut]);

  // Reset the timeout once Clerk finishes loading
  useEffect(() => {
    if (clerkLoaded && timeoutRef.current) clearTimeout(timeoutRef.current);
  }, [clerkLoaded]);

  const isLoading = !clerkLoaded && !timedOut;

  // Map Clerk user → our User shape
  const user: User | null = clerkUser
    ? {
        id: clerkUser.id,
        name:
          [clerkUser.firstName, clerkUser.lastName].filter(Boolean).join(" ") ||
          clerkUser.username ||
          clerkUser.primaryEmailAddress?.emailAddress?.split("@")[0] ||
          "Student",
        email: clerkUser.primaryEmailAddress?.emailAddress || "",
        avatar: clerkUser.imageUrl || undefined,
        website: clerkUser.unsafeMetadata?.website
          ? String(clerkUser.unsafeMetadata.website)
          : undefined,
        socialMedia: {
          instagram: clerkUser.unsafeMetadata?.instagram ? String(clerkUser.unsafeMetadata.instagram) : undefined,
	          facebook: clerkUser.unsafeMetadata?.facebook ? String(clerkUser.unsafeMetadata.facebook) : undefined,
	          snapchat: clerkUser.unsafeMetadata?.snapchat ? String(clerkUser.unsafeMetadata.snapchat) : undefined,
	          youtube: clerkUser.unsafeMetadata?.youtube ? String(clerkUser.unsafeMetadata.youtube) : undefined,
	        },
        studentProfile: {
          studentEmail: String(clerkUser.unsafeMetadata?.studentEmail ?? ""),
          course: String(clerkUser.unsafeMetadata?.course ?? ""),
          accommodation: String(clerkUser.unsafeMetadata?.accommodation ?? ""),
          university: String(clerkUser.unsafeMetadata?.university ?? ""),
          yearOfStudy: clerkUser.unsafeMetadata?.yearOfStudy
            ? String(clerkUser.unsafeMetadata.yearOfStudy)
            : undefined,
          onboardingComplete: clerkUser.unsafeMetadata?.onboardingComplete === true,
          studentEmailVerified: clerkUser.unsafeMetadata?.studentEmailVerified === true,
          studentEmailToken: clerkUser.unsafeMetadata?.studentEmailToken
            ? String(clerkUser.unsafeMetadata.studentEmailToken)
            : undefined,
        },
      }
    : null;

  // ── updateProfile ──────────────────────────────────────────────────────────
  const updateProfile = useCallback(async (data: ProfileUpdate) => {
    if (!clerkUser) return { success: false, error: "Not signed in." };
    try {
      const updatePayload: { unsafeMetadata?: Record<string, any>; firstName?: string; lastName?: string } = {};
      // Name fields go into Clerk's top-level (not unsafeMetadata)
      if (data.firstName !== undefined) updatePayload.firstName = data.firstName;
      if (data.lastName !== undefined) updatePayload.lastName = data.lastName;
      // Everything else goes into unsafeMetadata
      updatePayload.unsafeMetadata = {
        ...clerkUser.unsafeMetadata,
        ...(data.yearOfStudy !== undefined && { yearOfStudy: data.yearOfStudy }),
        ...(data.university !== undefined && { university: data.university }),
        ...(data.studentEmail !== undefined && { studentEmail: data.studentEmail }),
        ...(data.website !== undefined && { website: data.website }),
        ...(data.onboardingComplete !== undefined && { onboardingComplete: data.onboardingComplete }),
        ...(data.instagram !== undefined && { instagram: data.instagram }),
        ...(data.facebook !== undefined && { facebook: data.facebook }),
        ...(data.snapchat !== undefined && { snapchat: data.snapchat }),
        ...(data.studentEmailVerified !== undefined && { studentEmailVerified: data.studentEmailVerified }),
        ...(data.studentEmailToken !== undefined && {
          studentEmailToken: data.studentEmailToken === null ? undefined : data.studentEmailToken,
        }),
      };
      await clerkUser.update(updatePayload);
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err?.errors?.[0]?.message ?? "Failed to save profile." };
    }
  }, [clerkUser]);

  // ── sendVerificationEmail ──────────────────────────────────────────────────
  const sendVerificationEmail = useCallback(async (studentEmail: string) => {
    if (!clerkUser) return { success: false, error: "Not signed in." };
    try {
      const token = crypto.randomUUID();
      // Store the token and mark as unverified/pending
      await clerkUser.update({
        unsafeMetadata: {
          ...clerkUser.unsafeMetadata,
          studentEmail,
          studentEmailVerified: false,
          studentEmailToken: token,
        },
      });
      // Ask the edge function to send the verification email
      const verifyUrl = `${window.location.origin}/hub/verify-email?t=${token}&u=${clerkUser.id}`;
      const res = await fetch(`${API_BASE}/send-student-verification`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${publicAnonKey}`,
        },
        body: JSON.stringify({
          studentEmail,
          token,
          userId: clerkUser.id,
          userName: clerkUser.firstName || "Student",
          verifyUrl,
        }),
      });
      if (!res.ok) {
        // Edge function not yet extended — token is still stored so we can retry
        return { success: false, error: "Could not send email right now. Please try again shortly." };
      }
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err?.message ?? "Failed to send verification email." };
    }
  }, [clerkUser]);

  // ── loginWithGoogle ────────────────────────────────────────────────────────
  const loginWithGoogle = useCallback(async () => {
    if (!signIn) return;
    await signIn.authenticateWithRedirect({
      strategy: "oauth_google",
      redirectUrl: `${window.location.origin}/sso-callback`,
      redirectUrlComplete: `${window.location.origin}/hub`,
    });
  }, [signIn]);

  // ── login ──────────────────────────────────────────────────────────────────
  const login = useCallback(
    async (email: string, password: string) => {
      if (!signIn) return { success: false, error: "Auth not ready." };
      try {
        const result = await signIn.create({ identifier: email, password });
        if (result.status === "complete") {
          await setSignInActive({ session: result.createdSessionId });
          return { success: true };
        }
        return { success: false, error: "Sign-in incomplete — check your email for a verification link." };
      } catch (err: any) {
        const msg =
          err?.errors?.[0]?.longMessage ||
          err?.errors?.[0]?.message ||
          "Incorrect email or password.";
        return { success: false, error: msg };
      }
    },
    [signIn, setSignInActive]
  );

  // ── signup ─────────────────────────────────────────────────────────────────
  const signup = useCallback(
    async (
      email: string,
      password: string | undefined,
      name: string,
      profile?: Record<string, any>
    ) => {
      const isWaitlist = !password;

      // Waitlist flow: store in Supabase edge function only — no Clerk account.
      if (isWaitlist) {
        try {
          const res = await fetch(`${API_BASE}/signup`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${publicAnonKey}`,
            },
            body: JSON.stringify({ email, name, ...profile }),
          });
          const body = await res.json();
          if (!res.ok) {
            return { success: false, error: body.error || `Signup failed (HTTP ${res.status})` };
          }
          return { success: true };
        } catch (err: any) {
          return { success: false, error: err.message || "Network error during signup." };
        }
      }

      // Full account signup via Clerk.
      if (!signUp) return { success: false, error: "Auth not ready." };
      try {
        const nameParts = name.trim().split(" ");
        const result = await signUp.create({
          emailAddress: email,
          password,
          firstName: nameParts[0],
          lastName: nameParts.slice(1).join(" ") || undefined,
          unsafeMetadata: profile ?? {},
        });

        if (result.status === "complete") {
          await setSignUpActive({ session: result.createdSessionId });
          return { success: true };
        }

        // Email verification required
        if (result.status === "missing_requirements") {
          await result.prepareEmailAddressVerification({ strategy: "email_code" });
          return { success: false, needsVerification: true, error: "Check your email — we've sent a 6-digit code." };
        }

        return { success: false, error: "Sign-up incomplete." };
      } catch (err: any) {
        const msg =
          err?.errors?.[0]?.longMessage ||
          err?.errors?.[0]?.message ||
          "Sign-up failed.";
        return { success: false, error: msg };
      }
    },
    [signUp, setSignUpActive]
  );

  // ── confirmSignupCode ──────────────────────────────────────────────────────
  const confirmSignupCode = useCallback(async (code: string) => {
    if (!signUp) return { success: false, error: "Auth not ready." };
    try {
      const result = await signUp.attemptEmailAddressVerification({ code });
      if (result.status === "complete") {
        await setSignUpActive({ session: result.createdSessionId });
        return { success: true };
      }
      return { success: false, error: "Verification incomplete." };
    } catch (err: any) {
      const msg =
        err?.errors?.[0]?.longMessage ||
        err?.errors?.[0]?.message ||
        "Invalid or expired code. Try again.";
      return { success: false, error: msg };
    }
  }, [signUp, setSignUpActive]);

  // ── logout ─────────────────────────────────────────────────────────────────
  const logout = useCallback(async () => {
    await signOut();
  }, [signOut]);

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        loginWithGoogle,
        updateProfile,
        sendVerificationEmail,
        signup,
        confirmSignupCode,
        logout,
        isAuthenticated: !!isSignedIn,
        isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// ─── Hooks ───────────────────────────────────────────────────────────────────

export function useStudentAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useStudentAuth must be used within an AuthProvider");
  return ctx;
}

export const useAuth_  = useStudentAuth; // internal alias (avoid clash with Clerk's useAuth)
export const useUniAuth = useStudentAuth;

// Keep the old name so nothing else needs updating
export { useStudentAuth as useAuth };
